/**
 * WHO IS THIS MESSAGE ACTUALLY GOING TO? (extracted 2026-08-27, for #324)
 *
 * ---- ⛔⛔ WHY THIS IS A MODULE AND NOT A SECOND COPY -----------------------
 *
 * Every rule below was learned from a real send that went wrong, in
 * `SendDirectionsDialog`, between 2026-08-13 and 2026-08-17. The delivery pin
 * (#324) needs the same rules. ⛔ COPYING THEM WOULD MEAN ONE DIALOG GETS THE
 * NEXT FIX and the other keeps the bug — and the one that keeps it would be
 * whichever nobody happened to be looking at.
 *
 * ⭐ THE SAME REASONING THAT MADE `placeLines` / `placeHtml` / `placeSmsBits`
 * shared between the facility message and the pin. Two implementations of one
 * rule is a rule with a half-life.
 *
 * ---- ⚠️ WHAT IS DELIBERATELY *NOT* HERE ------------------------------------
 *
 * ⛔ NO PHONE-NUMBER FORMAT CHECK. The notification engine normalises numbers
 * centrally (fixed 2026-08-16) and rejects what it cannot parse with a message
 * naming the number. A second, weaker rule here would disagree with it — and the
 * weaker rule would win, because it runs first.
 */

export type Channel = 'email' | 'sms' | 'both';

export interface RecipientState {
    channel: Channel;
    /** Already committed to chips. */
    emails: string[];
    phones: string[];
    /** Still sitting in the input box, un-"Add"ed. */
    emailInput: string;
    phoneInput: string;
}

export interface RecipientPlan {
    ok: boolean;
    error?: string;
    /** The lists as they should now appear as chips. */
    emails: string[];
    phones: string[];
    /**
     * ⛔ WHETHER THE CALLER SHOULD COMMIT `emails`/`phones` TO ITS CHIPS AND CLEAR
     * THE INPUT BOXES, and it is FALSE ONLY for an unusable typed value.
     *
     * ⚠️ THE DISTINCTION IS NOT COSMETIC. A refusal like "add a phone number, or
     * switch to Email only" still commits, so the user SEES the address they
     * typed was accepted and only the missing half is at fault. An unparseable
     * address must NOT commit — clearing the box would delete the very text the
     * person needs to correct, and the error names a value no longer on screen.
     */
    commit: boolean;
}

/** Loose on purpose — the mail service is the authority, this only catches typos. */
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const wantsEmail = (c: Channel): boolean => c === 'email' || c === 'both';
export const wantsSms = (c: Channel): boolean => c === 'sms' || c === 'both';

/**
 * Fold the input boxes into the recipient lists and decide whether this send can
 * go.
 *
 * ⛔ RULE 1 — A VALUE TYPED BUT NOT "Add"-ed IS A RECIPIENT THE USER BELIEVES
 * THEY ENTERED. Before 2026-08-17, Send read only the committed chips: you typed
 * a phone number, pressed Send, and got "Add at least one recipient" — or worse,
 * it sent to the pre-filled drivers and silently dropped the number you had just
 * typed. Same trap as the roster Add button removed on 08-13.
 *
 * ⛔ RULE 2 — AN UNUSABLE TYPED VALUE IS REPORTED, NEVER DROPPED. Silently
 * discarding an unparseable address is the same bug wearing a different hat.
 *
 * ⛔ RULE 3 — "EMAIL + TEXT" MUST HAVE BOTH. The server does
 * `wantEmail && emails.length ? send : []` per channel, so picking both with an
 * empty email box texted the driver, skipped the email entirely, and reported
 * SUCCESS. ⚠️ That is the DEFAULT state of a dialog opened from an appointment —
 * driver phones pre-fill, emails do not — so "Email + Text" quietly meant "text"
 * nearly every time. Name the missing half rather than half-sending.
 *
 * ⚠️ ONLY THE CHANNELS IN PLAY ARE COLLECTED. A stray address left in the email
 * box must not block or join an SMS-only send.
 */
export function planRecipients(state: RecipientState): RecipientPlan {
    const wantEmail = wantsEmail(state.channel);
    const wantSms = wantsSms(state.channel);

    let emails = state.emails;
    let phones = state.phones;

    if (wantEmail) {
        const v = state.emailInput.trim();
        if (v) {
            // ⛔ NO COMMIT — see the note on `commit`. The bad text stays in the box.
            if (!EMAIL_RE.test(v)) {
                return { ok: false, commit: false, emails, phones, error: `Not a valid email address: ${v}` };
            }
            if (!emails.includes(v)) emails = [...emails, v];
        }
    }
    if (wantSms) {
        const v = state.phoneInput.trim();
        if (v && !phones.includes(v)) phones = [...phones, v];
    }

    const refuse = (error: string): RecipientPlan => ({ ok: false, commit: true, emails, phones, error });

    if (emails.length === 0 && phones.length === 0) return refuse('Add at least one recipient.');
    if (wantEmail && wantSms && emails.length === 0) return refuse('Add an email address, or switch to Text only.');
    if (wantEmail && wantSms && phones.length === 0) return refuse('Add a phone number, or switch to Email only.');
    if (wantEmail && !wantSms && emails.length === 0) return refuse('Add at least one email.');
    if (wantSms && !wantEmail && phones.length === 0) return refuse('Add at least one phone number.');

    return { ok: true, commit: true, emails, phones };
}
